import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Eye, EyeOff, Loader2, ArrowLeft, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSiteContent } from '../hooks/useSiteContent';

import { BACKEND_URL } from '../config/api';

export default function Login() {
  const navigate = useNavigate();
  const { get } = useSiteContent();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Please enter your email address first');
      return;
    }
    
    setResendingEmail(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, {
        email: email.trim()
      });
      toast.success('Verification email sent! Please check your inbox.');
      setShowResendVerification(false);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to resend verification email';
      toast.error(errorMessage);
    } finally {
      setResendingEmail(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    setShowResendVerification(false);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email: email.trim(),
        password: password
      }, {
        withCredentials: true
      });
      
      toast.success('Login successful!');
      
      // Navigate based on user type
      const userType = response.data.user_type;
      if (userType === 'tradesperson') {
        // Check if profile needs setup (new user or incomplete)
        try {
          const profileRes = await axios.get(`${BACKEND_URL}/api/tradespeople/profile/${response.data.user_id}`, {
            withCredentials: true
          });
          // If profile exists but not published or no availability, redirect to setup
          if (!profileRes.data.profile_status || profileRes.data.profile_status === 'draft' || profileRes.data.profile_visibility !== 'live') {
            navigate('/tradesperson/dashboard?setup=availability');
          } else {
            navigate('/tradesperson/dashboard');
          }
        } catch (_err) {
          // No profile yet - needs full setup
          navigate('/tradesperson/dashboard?setup=availability');
        }
      } else if (userType === 'employer') {
        navigate('/employer/dashboard');
      } else if (userType === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Login failed. Please try again.';
      toast.error(errorMessage);
      
      // Show resend verification option if email not verified
      if (errorMessage.toLowerCase().includes('verify') || errorMessage.toLowerCase().includes('email')) {
        setShowResendVerification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </button>
        
        <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Building2 className="h-8 w-8 text-orange-500" data-testid="login-logo" />
            <h1 className="text-2xl font-bold text-slate-900">OnsiteHand</h1>
          </div>
          
          <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center" data-testid="login-title">{get('auth.login.title', 'Sign In')}</h2>
          <p className="text-slate-600 text-center mb-6">{get('auth.login.subtitle', 'Welcome back to OnsiteHand')}</p>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">{get('auth.login.email_label', 'Email')}</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="w-full"
                data-testid="login-email-input"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">{get('auth.login.password_label', 'Password')}</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-orange-500 hover:text-orange-600"
                data-testid="forgot-password-link"
              >
                {get('auth.login.forgot_link', 'Forgot password?')}
              </button>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                get('auth.login.submit_btn', 'Sign In')
              )}
            </button>
            
            {/* Resend Verification Email Option */}
            {showResendVerification && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 mb-3">
                  Haven't received your verification email? Click below to resend it.
                </p>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendingEmail}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors disabled:opacity-50"
                  data-testid="resend-verification-btn"
                >
                  {resendingEmail ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      Resend Verification Email
                    </>
                  )}
                </button>
              </div>
            )}
          </form>
          
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-slate-500">{get('auth.login.or_continue', 'Or continue with')}</span>
              </div>
            </div>
            
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="btn-secondary w-full mt-4 flex items-center justify-center gap-2"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>
          </div>
          
          <p className="text-center text-sm text-slate-600 mt-6">
            {get('auth.login.no_account', "Don't have an account?")}{' '}
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="text-orange-500 font-medium hover:text-orange-600"
              data-testid="register-link"
            >
              {get('auth.login.register_link', 'Sign up')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
